/**
 * `GetDemandForecastDaily` response (`page`) から、チャート / グリッド表示用に
 * `KomaSlots` へ変換した派生データをまとめて算出する hook。
 *
 * index.tsx の God Component 化を避けるため、page-level の「proto スロット →
 * 表示用 KomaSlots」変換ロジックをここに集約する (review-all 指摘の一環)。
 */
import { useMemo } from 'react'

import type {
  DemandForecastDailyPage,
  SlotValue,
} from '@ebalance/proto/bffuser/v1/demand_forecast_service_pb'

import type { RetailerActualColumn } from '~/pages/DemandForecastDaily/models/pastActualRetailerSelection'
import {
  formatProtoDate,
  formatProtoMonthDay,
} from '~/pages/DemandForecastDaily/models/sectionHelpers'
import { protoSlotsToKoma } from '~/pages/DemandForecastDaily/models/slotHelpers'
import type { KomaSlots } from '~/pages/DemandForecastDaily/models/types'

/** proto SlotValue[] → KomaSlots 変換。空/undefined はそのまま undefined を返す。 */
function toWeatherKoma(
  slots: SlotValue[] | undefined,
  length: number,
): KomaSlots | undefined {
  if (slots === undefined || slots.length === 0) return undefined
  return protoSlotsToKoma(slots, length)
}

type Params = {
  page: DemandForecastDailyPage | undefined
}

export type DemandForecastGridData = {
  /** 当日実績スロット (today_actual_slots: 当日断面のみ) */
  todayActualKomaSlots: KomaSlots | undefined
  /** 気温予測スロット (backend が返す長さのまま。明日以前の対象日は 54 で index 48 = 24 時の点) */
  temperatureForecastKomaSlots: KomaSlots | undefined
  /** 気温実績スロット */
  temperatureActualKomaSlots: KomaSlots | undefined
  /** 露点実績スロット */
  dewpointActualKomaSlots: KomaSlots | undefined
  /**
   * 過去実績グリッドデータ (AC-001)。`id` は参照列「採用」(AC-DFD-047/047a/047b)
   * の列識別キー — 表示順に依存する KomaGrid 内部の React key
   * (`pa-${idx}-${label}`) とは別に、日付 (YYYY/MM/DD) 由来の安定 id を持たせる
   * ことで、列の並び替え・追加・削除があっても「採用中」の対象列がズレない
   * ようにする。
   *
   * `slots` はエリア合計 (proto `PastActual.slots` の KomaSlots 変換)。owner 軸の
   * per-retailer 選択 (Decision 2/6) は `retailerSlots` (KomaSlots 変換済み) /
   * `ownerBreakdownAvailable` を素通しし、`buildForecastSectionView`
   * (`selectPastActualColumn`) が isAggregateScope/currentEditRow と合わせて
   * 1 箇所で解決する (本 hook はこの選択を行わない — chart 向け `pastActuals`
   * と同じ規則を共有するため)。
   */
  pastActualGridData: Array<{
    id: string
    label: string
    slots: KomaSlots
    unattributedSlots?: KomaSlots
    retailerSlots: RetailerActualColumn[]
    ownerBreakdownAvailable: boolean
  }>
}

/** page の各種スロットを表示用 KomaSlots へ変換して返す。 */
export function useDemandForecastGridData({
  page,
}: Params): DemandForecastGridData {
  const todayActualKomaSlots = useMemo<KomaSlots | undefined>(() => {
    if (
      page?.todayActualSlots === undefined ||
      page.todayActualSlots.length === 0
    )
      return undefined
    return protoSlotsToKoma(page.todayActualSlots)
  }, [page?.todayActualSlots])

  // AC-03 (demand-forecast-daily-v4): 前日実績系列は過剰実装のため frontend
  // での参照を削除済み (page.prevDayActualSlots は proto field としては残置)。

  // 気温/露点スロット。長さ (48 / 54) は backend が返すデータで決まる (予報は
  // 対象日が明日以前なら 54、実績は翌日 0:00〜2:30 の観測がある時に 54。index 48
  // 以降は翌日 0:00〜) ので、 frontend は日付で判定し直さず最長の系列に揃える。
  const weatherSlotLength = useMemo(
    () =>
      Math.max(
        48,
        page?.temperatureForecastSlots?.length ?? 0,
        page?.temperatureActualSlots?.length ?? 0,
        page?.dewpointActualSlots?.length ?? 0,
      ),
    [
      page?.temperatureForecastSlots,
      page?.temperatureActualSlots,
      page?.dewpointActualSlots,
    ],
  )

  const temperatureForecastKomaSlots = useMemo<KomaSlots | undefined>(
    () => toWeatherKoma(page?.temperatureForecastSlots, weatherSlotLength),
    [page?.temperatureForecastSlots, weatherSlotLength],
  )

  const temperatureActualKomaSlots = useMemo<KomaSlots | undefined>(
    () => toWeatherKoma(page?.temperatureActualSlots, weatherSlotLength),
    [page?.temperatureActualSlots, weatherSlotLength],
  )

  const dewpointActualKomaSlots = useMemo<KomaSlots | undefined>(
    () => toWeatherKoma(page?.dewpointActualSlots, weatherSlotLength),
    [page?.dewpointActualSlots, weatherSlotLength],
  )

  // 過去実績グリッドデータ (AC-001)。owner 軸 field (retailerSlots /
  // ownerBreakdownAvailable) は KomaSlots 変換のみ行い素通しする (選択は
  // buildForecastSectionView に委ねる、上記 JSDoc 参照)。
  const pastActualGridData = useMemo(() => {
    if (page?.pastActuals === undefined) return []
    return page.pastActuals.map((pa) => ({
      id: formatProtoDate(pa.date),
      label: formatProtoMonthDay(pa.date) ?? '過去実績',
      slots: protoSlotsToKoma(pa.slots),
      unattributedSlots: protoSlotsToKoma(pa.unattributedSlots),
      retailerSlots: pa.retailerSlots.map((rs) => ({
        retailerBusinessCd: rs.retailerBusinessCd,
        slots: protoSlotsToKoma(rs.slots),
      })),
      ownerBreakdownAvailable: pa.ownerBreakdownAvailable,
    }))
  }, [page?.pastActuals])

  return {
    todayActualKomaSlots,
    temperatureForecastKomaSlots,
    temperatureActualKomaSlots,
    dewpointActualKomaSlots,
    pastActualGridData,
  }
}
